import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { Table, Input, Button, Tag, Image, message, Popconfirm, Segmented, Switch } from "antd";
import {
    SearchOutlined,
    DownloadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    PictureOutlined,
} from "@ant-design/icons";
import Layout from '../../Layout'
import useAuthStore from "../../store/authStore";
import * as XLSX from "xlsx";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";
import ProductFormModal from "./ProductFormModal";
import { htmlToText } from "../../utils/html";
import {
    getProducts,
    getAllProducts,
    deleteProduct,
    updateProductStatus,
    firstErrorMessage,
    type ProductItem,
} from "../../api/ProductAdmin";

const formatPrice = (n: number | string | null | undefined) => Number(n || 0).toLocaleString("vi-VN") + " đ";

/** Ảnh thay thế khi link avatar hỏng */
const IMAGE_FALLBACK = "data:image/svg+xml;utf8," + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44"><rect width="44" height="44" fill="#f0f0f0"/>' +
    '<path d="M14 29l5-6 4 4 3-3 4 5z" fill="#bfbfbf"/><circle cx="26" cy="17" r="2.5" fill="#bfbfbf"/></svg>'
);

/** Tab trạng thái -> tham số status của API (0: đang kinh doanh, 1: tạm ngưng) */
type StatusTab = "all" | "active" | "inactive";
const STATUS_PARAM: Record<StatusTab, number | undefined> = { all: undefined, active: 0, inactive: 1 };

interface Query {
    page: number;
    take: number;
    search: string;
    tab: StatusTab;
}

export default function ProductList() {
    const { t } = useTranslation();
    // Phân quyền thao tác trên sản phẩm
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("products.create");
    const canEdit = isSuperAdmin || hasPermission("products.edit");
    const canDelete = isSuperAdmin || hasPermission("products.delete");
    const canExport = isSuperAdmin || hasPermission("products.export");

    const [query, setQuery] = useState<Query>({ page: 1, take: 20, search: "", tab: "all" });
    const [keyword, setKeyword] = useState("");
    const [products, setProducts] = useState<ProductItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [togglingId, setTogglingId] = useState<number | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<ProductItem | null>(null);
    /** Bỏ kết quả của request cũ khi người dùng gõ / đổi trang nhanh */
    const requestSeq = useRef(0);

    const fetchData = async (q: Query = query) => {
        const seq = ++requestSeq.current;
        setLoading(true);
        try {
            const res = await getProducts({
                page: q.page,
                take: q.take,
                search: q.search || undefined,
                status: STATUS_PARAM[q.tab],
            });
            if (seq !== requestSeq.current) return;
            if (!res.status) {
                message.error(res.msg || t('products.load_failed'));
                return;
            }
            const items = res.data?.items || [];
            // Xóa hết dòng của trang cuối -> lùi về trang trước
            if (!items.length && q.page > 1 && (res.data?.total || 0) > 0) {
                setQuery(prev => ({ ...prev, page: prev.page - 1 }));
                return;
            }
            setProducts(items);
            setTotal(res.data?.total || 0);
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(query);
    }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

    // Tìm kiếm phía server, chờ người dùng ngừng gõ mới gọi API
    useEffect(() => {
        const timer = setTimeout(() => {
            const search = keyword.trim();
            setQuery(prev => prev.search === search ? prev : { ...prev, search, page: 1 });
        }, 400);
        return () => clearTimeout(timer);
    }, [keyword]);

    /* ===== ACTIONS ===== */
    const openAdd = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const openEdit = (record: ProductItem) => {
        setEditing(record);
        setModalOpen(true);
    };

    const handleSaved = () => {
        setModalOpen(false);
        fetchData();
    };

    const handleDelete = async (id: number) => {
        const res = await deleteProduct(id);
        if (!res.status) {
            message.error(firstErrorMessage(res) || t('products.delete_failed'));
            return;
        }
        message.success(res.msg || t('products.deleted'));
        fetchData();
    };

    const handleToggleStatus = async (record: ProductItem, active: boolean) => {
        setTogglingId(record.id);
        try {
            const status = active ? 0 : 1;
            const res = await updateProductStatus(record.id, status);
            if (!res.status) {
                message.error(firstErrorMessage(res) || t('products.update_failed'));
                return;
            }
            message.success(t(active ? 'products.status_on' : 'products.status_off', { name: record.name }));
            // Đang lọc theo trạng thái thì dòng vừa đổi không còn thuộc tab -> tải lại
            if (query.tab !== "all") fetchData();
            else setProducts(prev => prev.map(p => p.id === record.id ? { ...p, status } : p));
        } finally {
            setTogglingId(null);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const all = await getAllProducts({ search: query.search || undefined, status: STATUS_PARAM[query.tab] });
            if (!all.length) {
                message.warning(t('products.export_empty'));
                return;
            }
            const rows = all.map(p => ({
                [t('products.label_code')]: p.code,
                [t('products.label_barcode')]: p.code_2 || "",
                [t('products.label_name')]: p.name,
                [t('products.label_unit')]: p.unit || "",
                [t('products.label_price')]: Number(p.price) || 0,
                [t('products.label_discount_price')]: p.discount_price ? Number(p.discount_price) : "",
                [t('products.label_priority')]: p.priority ?? 0,
                [t('products.label_status')]: p.status === 0 ? t('products.status_active') : t('products.status_inactive'),
                // Mô tả lưu dạng HTML (trình soạn thảo) – Excel chỉ lấy chữ
                [t('products.label_description')]: htmlToText(p.description || ""),
            }));
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "Products");
            XLSX.writeFile(workbook, `san-pham_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } finally {
            setExporting(false);
        }
    };

    /* ===== COLUMNS ===== */
    const columns = [
        {
            key: "avatar",
            title: t('products.col_image'),
            dataIndex: "avatar",
            width: 80,
            render: (src: string | null) => src ? (
                <Image src={src} width={44} height={44} fallback={IMAGE_FALLBACK} className="rounded-md object-cover" />
            ) : (
                <div className="w-11 h-11 rounded-md bg-gray-100 dark:bg-[#262626] flex items-center justify-center text-gray-400">
                    <PictureOutlined />
                </div>
            ),
        },
        {
            key: "name",
            title: t('products.col_name'),
            dataIndex: "name",
            render: (_: any, record: ProductItem) => (
                <div className="min-w-[200px]">
                    <div
                        className={`font-medium ${canEdit ? "text-[#3e91ab] cursor-pointer hover:underline" : ""}`}
                        onClick={canEdit ? () => openEdit(record) : undefined}
                    >
                        {record.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        {record.code}
                        {record.code_2 && <span className="text-gray-400"> · {record.code_2}</span>}
                    </div>
                </div>
            ),
        },
        {
            key: "unit",
            title: t('products.col_unit'),
            dataIndex: "unit",
            width: 100,
            render: (val: string | null) => val || "-",
        },
        {
            key: "price",
            title: t('products.col_price'),
            dataIndex: "price",
            width: 160,
            align: "right" as const,
            render: (_: any, record: ProductItem) => {
                const discount = Number(record.discount_price || 0);
                const hasDiscount = discount > 0 && discount < Number(record.price);
                return hasDiscount ? (
                    <div>
                        <div className="font-semibold text-red-500">{formatPrice(discount)}</div>
                        <div className="text-xs text-gray-400 line-through">{formatPrice(record.price)}</div>
                    </div>
                ) : (
                    <div className="font-semibold">{formatPrice(record.price)}</div>
                );
            },
        },
        {
            key: "priority",
            title: t('products.col_priority'),
            dataIndex: "priority",
            width: 100,
            align: "center" as const,
        },
        {
            key: "status",
            title: t('products.col_status'),
            dataIndex: "status",
            width: 150,
            render: (status: number, record: ProductItem) => canEdit ? (
                <Switch
                    size="small"
                    checked={status === 0}
                    loading={togglingId === record.id}
                    onChange={(checked) => handleToggleStatus(record, checked)}
                    checkedChildren={t('products.status_active')}
                    unCheckedChildren={t('products.status_inactive')}
                />
            ) : (
                <Tag color={status === 0 ? "green" : "default"}>
                    {status === 0 ? t('products.status_active') : t('products.status_inactive')}
                </Tag>
            ),
        },
        {
            key: "actions",
            title: t('products.col_action'),
            width: 100,
            fixed: "right" as const,
            lockVisible: true,
            render: (_: any, record: ProductItem) => (
                <div className="flex items-center gap-3 text-[16px]">
                    {canEdit && (
                        <EditOutlined
                            title={t('products.edit')}
                            onClick={() => openEdit(record)}
                            className="text-blue-500 cursor-pointer"
                        />
                    )}
                    {canDelete && (
                        <Popconfirm
                            title={t('products.delete_title')}
                            description={t('products.delete_confirm')}
                            okText={t('products.delete')}
                            okType="danger"
                            cancelText={t('products.cancel')}
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <DeleteOutlined title={t('products.delete')} className="text-red-500 cursor-pointer" />
                        </Popconfirm>
                    )}
                </div>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "product_list",
        (canEdit || canDelete) ? columns : columns.filter((c: any) => c.key !== "actions")
    );

    return (
        <Layout>
            <Helmet>
                <title>{t('page.products')} | Khothi</title>
            </Helmet>

            <h2 className="text-lg text-gray-900 font-semibold t-text-white mb-4">
                {t('products.title')}
            </h2>

            {/* TOOLBAR */}
            <div className="p-4 bg-white dark:bg-[#141414] rounded-lg mb-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <Segmented<StatusTab>
                    value={query.tab}
                    onChange={(tab) => setQuery(prev => ({ ...prev, tab, page: 1 }))}
                    options={[
                        { value: "all", label: t('products.tab_all') },
                        { value: "active", label: t('products.status_active') },
                        { value: "inactive", label: t('products.status_inactive') },
                    ]}
                />

                <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                        allowClear
                        prefix={<SearchOutlined className="text-gray-400" />}
                        placeholder={t('products.search')}
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="w-full sm:w-[300px]"
                    />
                    <div className="flex gap-2">
                        {canExport && (
                            <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
                                {t('products.export_excel')}
                            </Button>
                        )}
                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openAdd}
                                className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                            >
                                {t('products.add')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">
                    {t('products.total', { count: total })}
                </span>
                <ColumnSettingButton {...columnSettingProps} />
            </div>

            {/* TABLE */}
            <Table
                columns={tableColumns}
                components={tableComponents}
                tableLayout={tableLayout}
                dataSource={products}
                loading={loading}
                rowKey="id"
                scroll={{ x: scrollX }}
                className="custom-ant-table"
                locale={{ emptyText: query.search ? t('products.empty_search') : t('products.empty') }}
                pagination={{
                    current: query.page,
                    pageSize: query.take,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    onChange: (page, take) => setQuery(prev => ({ ...prev, page: take !== prev.take ? 1 : page, take })),
                }}
            />

            <ProductFormModal
                open={modalOpen}
                product={editing}
                onClose={() => setModalOpen(false)}
                onSaved={handleSaved}
            />
        </Layout>
    );
}
